import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { signUpErrorMessage } from '../../services/betaSignUp';
import { listDeletedEvents, restoreDeletedEvent, type DeletedEventRow } from '../../services/adminEvents';

const sectionClass = 'bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4';
const headingClass = 'text-lg font-semibold text-gray-900 dark:text-white';

function formatDeletedAt(iso: string | null): string {
  if (!iso) return 'Unknown';
  return new Date(iso).toLocaleString();
}

function djLabel(row: DeletedEventRow): string {
  return row.djName ?? row.djEmail ?? 'this DJ';
}

export function DeletedEventsSection() {
  const [rows, setRows] = useState<DeletedEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busyEventId, setBusyEventId] = useState<string | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setRows(await listDeletedEvents());
    } catch (error) {
      console.error('List deleted events failed', error);
      setLoadError('Could not load the deleted events. Try Refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleRestore = async (row: DeletedEventRow) => {
    if (!window.confirm(`Put "${row.name}" back on ${djLabel(row)}'s dashboard?`)) return;
    setBusyEventId(row.eventId);
    try {
      await restoreDeletedEvent(row.eventId);
      toast.success('Event restored');
      await loadRows();
    } catch (error) {
      console.error('Restore deleted event failed', error);
      toast.error(signUpErrorMessage(error));
    } finally {
      setBusyEventId(null);
    }
  };

  return (
    <section className={sectionClass}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className={headingClass}>Deleted events</h2>
        <button type="button" className="btn btn-secondary" onClick={loadRows} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Events DJs deleted. Their requests, votes and messages are kept. Restore puts the event back on the DJ&apos;s dashboard.
      </p>

      {loadError && (
        <div role="alert" className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {loadError}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading deleted events…</p>
      ) : !loadError && rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No deleted events.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.eventId} data-testid="deleted-event-row" className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white">{row.name}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Event date: {row.date ?? 'Unknown'}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
                    {row.djAccountExists
                      ? `DJ: ${row.djName ?? '(no name)'}${row.djEmail ? ` · ${row.djEmail}` : ''}`
                      : 'DJ: Account deleted'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Deleted {formatDeletedAt(row.deletedAt)} · {row.requestCount} {row.requestCount === 1 ? 'request' : 'requests'}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={!row.djAccountExists || busyEventId === row.eventId}
                  title={row.djAccountExists ? undefined : 'DJ account no longer exists'}
                  onClick={() => handleRestore(row)}
                >
                  {busyEventId === row.eventId ? 'Restoring…' : 'Restore to DJ'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
